// Shared helpers for the MapLibre + MapTiler Alpine components (public display map
// and admin editable maps). Extracted from the homepage PoC so both sides stay in sync.

const MAPTYPE_TO_STYLE = {
  roadmap: 'streets-v2',
  satellite: 'satellite',
  hybrid: 'hybrid',
  terrain: 'outdoor-v2',
};

const DEFAULT_STYLE = MAPTYPE_TO_STYLE.roadmap;
export const DEFAULT_ZOOM = 14;
export const TARGET_ICON_HEIGHT = 40;
export const DEFAULT_ICON_ID = 'topic-icon-default';

// Default simplestyle paint, applied to features that don't carry their own
// fill/stroke properties. Shared by the admin and public GeoJSON layers.
const DEFAULT_FEATURE_COLOR = '#3388ff';

// Brand red for the default map pin, matching the legacy marker PNG (#f45a37).
// Kept separate from DEFAULT_FEATURE_COLOR so it doesn't recolour GeoJSON shapes.
const DEFAULT_MARKER_COLOR = '#f45a37';
const DEFAULT_FILL_OPACITY = 0.2;
const DEFAULT_STROKE_OPACITY = 1;
const DEFAULT_LINE_WIDTH = 2;
const OVERLAY_RASTER_OPACITY = 0.85;

// Source / layer ids for the ground-image overlay and the project GeoJSON, shared so the
// admin (editable) and public (display) maps add and remove the same layers.
export const OVERLAY_SOURCE_ID = 'overlay-image';
export const OVERLAY_LAYER_ID = 'overlay-image-layer';
export const GEOJSON_SOURCE_ID = 'project-geojson';
export const GEOJSON_FILL_LAYER_ID = 'project-geojson-fill';
export const GEOJSON_LINE_LAYER_ID = 'project-geojson-line';
export const GEOJSON_POINT_LAYER_ID = 'project-geojson-point';

const DEFAULT_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="52" viewBox="0 0 40 52">
  <path d="M20 0C9 0 0 9 0 20c0 14 20 32 20 32s20-18 20-32C40 9 31 0 20 0z" fill="${DEFAULT_MARKER_COLOR}" stroke="white" stroke-width="2"/>
  <circle cx="20" cy="20" r="7" fill="white"/>
</svg>`;

// The default pin as an inline data URI — the single source of truth for the
// "no custom marker" fallback. Inline (not the S3 PNG) so it needs no S3 request
// and no CORS: usable both as a DOM <img> src and a WebGL texture.
export function defaultMarkerDataUri() {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(DEFAULT_ICON_SVG)}`;
}

export function styleUrlFor(mapType, apiKey) {
  const style = MAPTYPE_TO_STYLE[mapType] || DEFAULT_STYLE;
  return `https://api.maptiler.com/maps/${style}/style.json?key=${apiKey}`;
}

// Localized help text for MapLibre's cooperativeGestures overlay. The strings are
// rendered per-locale onto <html> by the layouts (see ApplicationHelper#map_zoom_hint_data);
// any that are absent fall back to MapLibre's built-in English defaults.
export function cooperativeGesturesLocale() {
  const d = document.documentElement.dataset;
  const locale = {};
  if (d.mapZoomHintMac) locale['CooperativeGesturesHandler.MacHelpText'] = d.mapZoomHintMac;
  if (d.mapZoomHintWindows) locale['CooperativeGesturesHandler.WindowsHelpText'] = d.mapZoomHintWindows;
  if (d.mapZoomHintMobile) locale['CooperativeGesturesHandler.MobileHelpText'] = d.mapZoomHintMobile;
  return locale;
}

// Loads an image with CORS enabled so MapLibre can sample it into a WebGL texture
// (custom marker icons, ground overlays). See docs/maplibre-homepage-poc.md "CORS" note.
function loadImageElement(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

// Registers a marker image as a WebGL texture under `imageId`, scaling its
// pixelRatio so tall source images render at a sensible on-map size. Shared by
// the admin and public maps so their pin rendering stays identical. A load
// failure (e.g. a non-CORS URL) is logged and swallowed: callers rely on the
// layer's `coalesce` to fall back to the default pin.
export async function loadMapIcon(map, imageId, url) {
  if (!url || map.hasImage(imageId)) return;
  try {
    const img = await loadImageElement(url);
    const pixelRatio = Math.max(1, img.naturalHeight / TARGET_ICON_HEIGHT);
    map.addImage(imageId, img, { pixelRatio });
  } catch (error) {
    console.error(`Failed to load icon ${url}`, error);
  }
}

// --- Layer spec builders (pure: return a fresh MapLibre layer/coords config) ---

// Ground-overlay image bounds -> MapLibre ImageSource coordinate ring (NW, NE, SE, SW).
export function imageOverlayCoordinates({ north, south, east, west }) {
  return [
    [west, north],
    [east, north],
    [east, south],
    [west, south],
  ];
}

export function imageRasterLayer() {
  return {
    id: OVERLAY_LAYER_ID,
    type: 'raster',
    source: OVERLAY_SOURCE_ID,
    paint: { 'raster-opacity': OVERLAY_RASTER_OPACITY },
  };
}

export function geoJsonFillLayer() {
  return {
    id: GEOJSON_FILL_LAYER_ID,
    type: 'fill',
    source: GEOJSON_SOURCE_ID,
    paint: {
      'fill-color': ['coalesce', ['get', 'fill'], DEFAULT_FEATURE_COLOR],
      'fill-opacity': ['coalesce', ['get', 'fill-opacity'], DEFAULT_FILL_OPACITY],
    },
    filter: ['==', '$type', 'Polygon'],
  };
}

export function geoJsonLineLayer() {
  return {
    id: GEOJSON_LINE_LAYER_ID,
    type: 'line',
    source: GEOJSON_SOURCE_ID,
    paint: {
      'line-color': ['coalesce', ['get', 'stroke'], DEFAULT_FEATURE_COLOR],
      'line-opacity': ['coalesce', ['get', 'stroke-opacity'], DEFAULT_STROKE_OPACITY],
      'line-width': ['coalesce', ['get', 'stroke-width'], DEFAULT_LINE_WIDTH],
    },
    filter: ['match', ['geometry-type'], ['Polygon', 'LineString'], true, false],
  };
}

// Point/MultiPoint features rendered as dots, like the markers Google's Data layer drew.
export function geoJsonPointLayer() {
  return {
    id: GEOJSON_POINT_LAYER_ID,
    type: 'circle',
    source: GEOJSON_SOURCE_ID,
    paint: {
      'circle-radius': 6,
      'circle-color': ['coalesce', ['get', 'marker-color'], DEFAULT_FEATURE_COLOR],
      'circle-stroke-color': '#ffffff',
      'circle-stroke-width': 2,
    },
    filter: ['==', '$type', 'Point'],
  };
}

// Adds the MapTiler place-search box to the map (lazy-loaded). On display maps the control
// just flies to the picked result; on editable/location-input maps, pass `onPick` to also
// move the marker and write the lat/lng inputs.
export async function addGeocodingControl(map, { apiKey, onPick } = {}) {
  if (!apiKey) return null;

  // Patched in .yarn/patches to a namespace import: it still default-imports maplibre-gl,
  // which v6 no longer exports. Drop the patch once upstream supports maplibre-gl 6.
  const { GeocodingControl } = await import('@maptiler/geocoding-control/maplibregl');

  const control = new GeocodingControl({
    apiKey,
    marker: false, // the host map manages its own marker; the control only flies/recenters
    showResultMarkers: false,
    proximity: [{ type: 'map-center' }], // bias results to the current view
  });

  map.addControl(control, 'top-left');

  if (onPick) {
    control.on('pick', (event) => {
      const center = event.feature?.center;
      if (center) onPick({ lng: center[0], lat: center[1] });
    });
  }

  return control;
}

// Under a bundler MapLibre cannot resolve its worker from import.meta.url, so setWorkerUrl
// is required. `?worker&url` (not plain `?url`) emits the sibling chunk the worker imports;
// with `?url` the worker loads in dev and fails in production.
let maplibrePromise = null;

export function loadMaplibre() {
  maplibrePromise ||= (async () => {
    const [maplibregl, { default: workerUrl }] = await Promise.all([
      import('maplibre-gl'),
      import('maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url'),
      import('maplibre-gl/dist/maplibre-gl.css'),
    ]);
    maplibregl.setWorkerUrl(workerUrl);
    return maplibregl;
  })();

  return maplibrePromise;
}
